/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Supervisor wallboard: who is on what, and listening in.
 *
 * Agents are tiles rather than table rows because a supervisor scans for who
 * needs attention, not for an alphabetical list — sorted on-call first, so the
 * calls that could go wrong are at the top.
 *
 * Monitor / whisper / barge are disabled rather than hidden when an agent is
 * not on a call, matching how the rest of this app gates actions. Supervising
 * switches the softphone dock into a distinctly-coloured mode, so a supervisor
 * always knows they are listening to somebody.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Groups, Hearing, RecordVoiceOver, Stop } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import InfoCard from "components/detail/InfoCard";
import MiniTable from "components/detail/MiniTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { useAuth } from "auth/Auth";
import { useWebSocket } from "context/WebSocketContext";
import { useTelephony } from "telephony/TelephonyContext";
import { formatDuration } from "telephony/callStates";
import { getApiErrorMessage } from "utils/apiErrors";

const REFRESH_MS = 5000;

const PRESENCE_COLOR = {
  available: "#1F8A56",
  on_call: "#1565C8",
  ringing: "#1565C8",
  wrap_up: "#7B1FA2",
  busy: "#E68A06",
  break: "#E68A06",
  lunch: "#E68A06",
  meeting: "#E68A06",
  training: "#E68A06",
  offline: "#717C96",
};

const SUPERVISE_PURPLE = "#7B1FA2";

function secondsSince(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

function AgentTile({ agent, onSupervise, supervising, busy, deviceReady }) {
  const color = PRESENCE_COLOR[agent.state] || PRESENCE_COLOR.offline;
  const onCall = agent.state === "on_call" && agent.conversation_id;
  const elapsed = secondsSince(onCall ? agent.call_started_at : agent.state_since);
  const isSupervised = supervising === agent.conversation_id;

  return (
    <MDBox
      sx={{
        p: 2,
        borderRadius: "12px",
        border: isSupervised ? `2px solid ${SUPERVISE_PURPLE}` : "1px solid #E8EDF5",
        bgcolor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <MDBox
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
            // A closed laptop cannot take a call; showing it as plain
            // "available" would make the wallboard overstate capacity.
            outline: agent.device_status !== "registered" ? "2px solid #E0E0E0" : "none",
          }}
        />
        <MDBox flexGrow={1} minWidth={0}>
          <MDTypography variant="button" fontWeight="bold" color="dark" display="block" noWrap>
            {agent.name}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {agent.state.replace("_", " ")}
            {agent.state_reason ? ` · ${agent.state_reason}` : ""} ·{" "}
            <MonoText variant="caption">{formatDuration(elapsed)}</MonoText>
          </MDTypography>
        </MDBox>
      </MDBox>

      {onCall && agent.counterparty && (
        <MonoText variant="caption" sx={{ color: "#5C6B82" }}>
          {agent.counterparty}
        </MonoText>
      )}

      <MDBox display="flex" gap={0.5} mt="auto">
        {[
          { mode: "monitor", icon: <Hearing fontSize="small" />, label: "Listen" },
          { mode: "whisper", icon: <RecordVoiceOver fontSize="small" />, label: "Whisper" },
          { mode: "barge", icon: <Groups fontSize="small" />, label: "Join" },
        ].map(({ mode, icon, label }) => (
          <Tooltip key={mode} title={onCall ? label : "Agent is not on a call"}>
            <span>
              <IconButton
                size="small"
                // Supervising opens a call leg to the supervisor's own
                // softphone. Without a registered Device that leg has nowhere
                // to land, and the request succeeds while nothing is audible.
                disabled={!onCall || busy || !deviceReady}
                onClick={() => onSupervise(agent, mode)}
                sx={{ color: isSupervised ? SUPERVISE_PURPLE : "#5C6B82" }}
              >
                {icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
        {isSupervised && (
          <Tooltip title="Stop supervising">
            <span>
              <IconButton
                size="small"
                onClick={() => onSupervise(agent, null)}
                sx={{ color: "#C62828", ml: "auto" }}
              >
                <Stop fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </MDBox>
    </MDBox>
  );
}

export default function SupervisorConsole() {
  const { axiosInstance } = useAuth();
  const { telephonyEvent } = useWebSocket();
  // Supervising dials the supervisor's own softphone, so it needs a Device.
  const { registered } = useTelephony();

  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [board, setBoard] = useState(null);
  const [queues, setQueues] = useState([]);
  const [queueId, setQueueId] = useState("");
  const [supervising, setSupervising] = useState(null);
  // The agent currently being acted on, not a global flag. A single boolean
  // disabled every supervise button on the board while one request was in
  // flight, so a supervisor watching twenty agents lost the whole wallboard
  // for the duration of one click.
  const [busyAgentId, setBusyAgentId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/telephony/supervisor/wallboard", {
        params: queueId ? { queue_id: queueId } : {},
      });
      setBoard(res.data);
      setDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else setError(getApiErrorMessage(err, "Could not load the wallboard."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, queueId]);

  useEffect(() => {
    axiosInstance
      .get("/telephony/supervisor/queues")
      .then((r) => setQueues(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQueues([]));
  }, [axiosInstance]);

  useEffect(() => {
    load();
    // Polled rather than purely event-driven: a wallboard that silently stops
    // updating because one socket message was missed is worse than useless.
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Refresh immediately on a call event rather than waiting for the poll.
  useEffect(() => {
    if (telephonyEvent) load();
  }, [telephonyEvent, load]);

  const supervise = async (agent, mode) => {
    setBusyAgentId(agent.user_id ?? agent.conversation_id);
    setError("");
    try {
      if (mode === null) {
        await axiosInstance.delete(`/telephony/supervisor/supervise/${agent.conversation_id}`);
        setSupervising(null);
        setToast("Stopped supervising.");
      } else {
        await axiosInstance.post("/telephony/supervisor/supervise", {
          conversation_id: agent.conversation_id,
          mode,
        });
        setSupervising(agent.conversation_id);
        setToast(
          mode === "monitor"
            ? `Listening to ${agent.name}.`
            : mode === "whisper"
            ? `Whispering to ${agent.name} — the customer cannot hear you.`
            : `Joined ${agent.name}'s call — everyone can hear you.`
        );
      }
      load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not supervise that call."));
    } finally {
      setBusyAgentId(null);
    }
  };

  const agents = board?.agents || [];
  // Memoised so the derived waitingRows below does not recompute every render.
  const queueRows = useMemo(() => board?.queues || [], [board]);

  const waitingRows = useMemo(
    () =>
      queueRows
        .filter((q) => q.waiting > 0)
        .map((q) => ({
          id: q.queue_id,
          name: q.name,
          waiting: q.waiting,
          longest: q.longest_wait_seconds,
          sla: q.sla_answer_seconds,
        })),
    [queueRows]
  );

  if (denied) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={2}>
          <Alert severity="info">
            You do not supervise any queues yet. An administrator can add you as a supervisor
            under Settings → Telephony → Queues.
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3} px={1}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Supervisor
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Live view of your queues
            </MDTypography>
          </MDBox>
          {/* A dropdown with one option is noise; show the name instead. */}
          {queues.length > 1 ? (
            <Select
              size="small"
              displayEmpty
              value={queueId}
              onChange={(e) => setQueueId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All my queues</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            queues.length === 1 && (
              <MDTypography variant="button" color="text">
                {queues[0].name}
              </MDTypography>
            )
          )}
        </MDBox>

        {loading && !board ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            <Grid container spacing={3} mb={1}>
              <Grid item xs={12} md={6} lg={3}>
                <ComplexStatisticsCard
                  color="info"
                  icon="hourglass_top"
                  title="Calls waiting"
                  count={board?.calls_waiting ?? 0}
                  percentage={{ color: "text", amount: "", label: "in your queues" }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ComplexStatisticsCard
                  color={
                    (board?.longest_wait_seconds || 0) > 60 ? "error" : "dark"
                  }
                  icon="timer"
                  title="Longest wait"
                  count={formatDuration(board?.longest_wait_seconds || 0)}
                  percentage={{ color: "text", amount: "", label: "current" }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ComplexStatisticsCard
                  color="success"
                  icon="headset_mic"
                  title="Available"
                  count={board?.agents_available ?? 0}
                  percentage={{
                    color: "text",
                    amount: "",
                    label: `${board?.agents_on_call ?? 0} on a call`,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="verified"
                  title="Service level"
                  count={
                    board?.service_level_pct === null || board?.service_level_pct === undefined
                      ? "—"
                      : `${board.service_level_pct}%`
                  }
                  percentage={{ color: "text", amount: "", label: "answered in target today" }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} lg={7}>
                <InfoCard icon="groups" title={`Agents (${agents.length})`}>
                  {agents.length === 0 ? (
                    <MDTypography variant="button" color="text">
                      No agents are members of your queues yet.
                    </MDTypography>
                  ) : (
                    <Grid container spacing={2}>
                      {agents.map((agent) => (
                        <Grid item xs={12} sm={6} md={4} key={agent.user_id}>
                          <AgentTile
                            agent={agent}
                            onSupervise={supervise}
                            supervising={supervising}
                            busy={busyAgentId === (agent.user_id ?? agent.conversation_id)}
                            deviceReady={registered}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </InfoCard>
              </Grid>

              <Grid item xs={12} lg={5}>
                <MDBox display="flex" flexDirection="column" gap={3}>
                  <InfoCard icon="queue" title="Waiting now">
                    <MiniTable
                      columns={[
                        { key: "name", label: "Queue", width: "1.5fr" },
                        { key: "waiting", label: "Waiting" },
                        {
                          key: "longest",
                          label: "Longest",
                          render: (r) => (
                            <MonoText
                              variant="button"
                              // Past the queue's own target, not a fixed number.
                              sx={{ color: r.longest > r.sla ? "#C62828" : "inherit" }}
                            >
                              {formatDuration(r.longest)}
                            </MonoText>
                          ),
                        },
                      ]}
                      rows={waitingRows}
                      emptyMessage="Nobody is waiting."
                    />
                  </InfoCard>

                  <InfoCard icon="analytics" title="Queues">
                    <MiniTable
                      columns={[
                        { key: "name", label: "Queue", width: "1.5fr" },
                        {
                          key: "available_agents",
                          label: "Available",
                          render: (r) => (
                            <StatusBadge
                              status={r.available_agents > 0 ? "available" : "offline"}
                              label={String(r.available_agents)}
                              size="xs"
                            />
                          ),
                        },
                        { key: "on_call_agents", label: "On call" },
                      ]}
                      rows={queueRows.map((q) => ({ ...q, id: q.queue_id }))}
                      emptyMessage="No queues."
                    />
                  </InfoCard>
                </MDBox>
              </Grid>
            </Grid>
          </>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="info" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
